import React from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useMediaQuery } from "react-responsive";

type PaginationPropType = {
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  currentPage: number;
};

const pageClass = (active: boolean) =>
  `inline-flex items-center justify-center min-w-[32px] h-8 px-2 rounded text-[13px] font-semibold tabular-nums transition-colors disabled:cursor-default ${
    active
      ? "bg-primary text-white shadow-xs"
      : "text-ink-2 hover:bg-surface-hover border border-transparent hover:border-line"
  }`;

const arrowClass =
  "inline-flex items-center justify-center w-8 h-8 rounded border border-line text-ink-2 bg-surface hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

const ellipsisClass = "inline-flex items-center justify-center w-6 h-8 text-ink-4 select-none";

export const Pagination = ({ setCurrentPage, totalPages, currentPage }: PaginationPropType) => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  let maxVisiblePages = totalPages > 10 ? 5 : 5;
  if (isMobile) {
    maxVisiblePages = totalPages > 10 ? 2 : 2;
  }
  const visiblePages = Array.from(
    { length: Math.min(totalPages, maxVisiblePages) },
    (_, i) => i + Math.max(currentPage - Math.floor(maxVisiblePages / 2), 1)
  ).filter((page) => page <= totalPages);

  return (
    <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="Pagination">
      <button
        type="button"
        disabled={totalPages < maxVisiblePages}
        onClick={() => setCurrentPage(1)}
        className={`${pageClass(currentPage === 1)} ${currentPage < 4 ? "hidden" : ""}`}
      >
        1
      </button>
      <button
        type="button"
        disabled={currentPage <= 1}
        onClick={() => setCurrentPage(currentPage - 1)}
        className={arrowClass}
        aria-label="Previous page"
      >
        <FiChevronLeft />
      </button>

      {currentPage > Math.floor(maxVisiblePages / 2) + 1 && <span className={ellipsisClass}>…</span>}
      {visiblePages.map((page) => (
        <button
          type="button"
          key={page}
          onClick={() => setCurrentPage(page)}
          className={pageClass(page === currentPage)}
          aria-current={page === currentPage ? "page" : undefined}
        >
          {page}
        </button>
      ))}
      {currentPage < totalPages - Math.floor(maxVisiblePages / 2) && <span className={ellipsisClass}>…</span>}

      <button
        type="button"
        disabled={currentPage >= totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
        className={arrowClass}
        aria-label="Next page"
      >
        <FiChevronRight />
      </button>
      {totalPages > 1 && (
        <button
          type="button"
          disabled={totalPages < maxVisiblePages}
          onClick={() => setCurrentPage(totalPages)}
          className={`${pageClass(currentPage === totalPages)} ${currentPage > totalPages - 2 ? "hidden" : ""}`}
        >
          {totalPages}
        </button>
      )}
    </nav>
  );
};

export default Pagination;
